/**
 * Data Transformer for Complex Process
 * 
 * Transforms raw GraphQL responses into frontend-ready data structures
 */

import {
    ComplexProcessDefinition,
    ConnectionType,
    ExternalSystem,
    ProcessComplexity,
    ProcessConnection,
    ProcessMetrics,
    ProcessStage,
    ProcessStep,
    StageType,
    StepType
} from '../types/ComplexProcess';
import { asBackendId } from '../../types/branded-ids';

export class ComplexProcessTransformer {
    /**
     * Transform a list response
     */
    transformList(response: any): ComplexProcessDefinition[] {
        if (!response?.items) return [];

        return response.items.map((item: any) => this.transformSingle(item));
    }

    /**
     * Transform a single process
     */
    transformSingle(data: any): ComplexProcessDefinition {
        if (!data) return this.getEmptyProcess();

        return {
            id: data.id,
            tenant: data.tenant || sessionStorage.getItem('tenant') || 'default',
            name: data.name,
            domain: data.domain,
            description: data.description || '',
            complexity: this.transformComplexity(data.complexity),
            avgDuration: data.avgDuration,
            stages: this.transformStages(data.stages),
            connections: this.transformConnections(data.connections),
            actors: this.transformActors(data.actors),
            externalSystems: this.transformExternalSystems(data.externalSystems),
            metrics: this.transformMetrics(data.metrics),
            isTemplate: data.isTemplate || false,
            version: data.version || '1.0.0',
            parentId: data.parentId,
            createdAt: this.transformDate(data.createdAt),
            updatedAt: this.transformDate(data.updatedAt),
            createdBy: data.createdBy || 'system',
            active: data.active !== false,
            metadata: data.metadata || {}
        };
    }

    /**
     * Transform stages array
     */
    private transformStages(stages: any[]): ProcessStage[] {
        if (!stages || !Array.isArray(stages)) return [];

        return stages.map(stage => ({
            id: stage.id,
            name: stage.name,
            stageType: this.transformStageType(stage.stageType),
            actors: stage.actors || [],
            steps: this.transformSteps(stage.steps),
            loops: stage.loops || false,
            parallelTracks: stage.parallelTracks || [],
            estimatedDuration: stage.estimatedDuration,
            position: stage.position || { x: 0, y: 0 },
            color: stage.color,
            metadata: stage.metadata || {}
        }));
    }

    /**
     * Transform steps array
     */
    private transformSteps(steps: any[]): ProcessStep[] {
        if (!steps || !Array.isArray(steps)) return [];

        return steps.map(step => ({
            id: step.id,
            name: step.name,
            actor: step.actor,
            stepType: this.transformStepType(step.stepType),
            external: step.external || false,
            conditional: step.conditional || false,
            loops: step.loops || false,
            triggers: step.triggers || [],
            outputs: step.outputs || [],
            estimatedDuration: step.estimatedDuration,
            metadata: step.metadata || {}
        }));
    }

    /**
     * Transform connections array
     */
    private transformConnections(connections: any[]): ProcessConnection[] {
        if (!connections || !Array.isArray(connections)) return [];

        return connections.map(conn => ({
            id: conn.id || `${conn.from}_${conn.to}`,
            from: conn.from,
            to: conn.to,
            connectionType: this.transformConnectionType(conn.connectionType),
            condition: conn.condition,
            label: conn.label,
            probability: conn.probability,
            avgTransitionTime: conn.avgTransitionTime,
            metadata: conn.metadata || {}
        }));
    }

    /**
     * Transform external systems
     */
    private transformExternalSystems(systems: any[]): ExternalSystem[] {
        if (!systems || !Array.isArray(systems)) return [];

        return systems.map(system => ({
            id: system.id,
            name: system.name,
            systemType: system.systemType,
            integrationPoints: system.integrationPoints || [],
            dataFormats: system.dataFormats || [],
            authentication: system.authentication,
            metadata: system.metadata || {}
        }));
    }

    /**
     * Transform metrics
     */
    private transformMetrics(metrics: any): ProcessMetrics {
        if (!metrics) return this.getDefaultMetrics();

        return {
            totalSteps: metrics.totalSteps || 0,
            uniqueActors: metrics.uniqueActors || 0,
            loopingStages: metrics.loopingStages || 0,
            externalIntegrations: metrics.externalIntegrations || 0,
            decisionPoints: metrics.decisionPoints || 0,
            avgComplexityScore: metrics.avgComplexityScore || 0,
            estimatedTotalDuration: metrics.estimatedTotalDuration
        };
    }

    /**
     * Transform actors (ensure unique)
     */
    private transformActors(actors: any): string[] {
        if (!actors) return [];
        if (typeof actors === 'string') return [actors];
        if (!Array.isArray(actors)) return [];

        // Remove duplicates
        return [...new Set(actors.filter((a) => typeof a === 'string'))];
    }

    /**
     * Transform complexity enum
     */
    private transformComplexity(complexity: any): ProcessComplexity {
        const valid: ProcessComplexity[] = ['Low', 'Medium', 'High', 'Very High'];

        if (valid.includes(complexity)) {
            return complexity;
        }

        // Default based on some heuristics
        return 'Medium';
    }

    /**
     * Transform stage type
     */
    private transformStageType(type: any): StageType {
        const valid: StageType[] = ['Entry', 'Process', 'Decision', 'External', 'Loop', 'Meta', 'Exit'];

        if (valid.includes(type)) {
            return type;
        }

        return 'Process';
    }

    /**
     * Transform step type
     */
    private transformStepType(type: any): StepType | undefined {
        if (!type) return undefined;

        const valid: StepType[] = [
            'Manual', 'Automated', 'Approval', 'Review',
            'Integration', 'Transform', 'Decision', 'Escalation', 'Notification'
        ];

        if (valid.includes(type)) {
            return type;
        }

        return undefined;
    }

    /**
     * Transform connection type
     */
    private transformConnectionType(type: any): ConnectionType {
        const valid: ConnectionType[] = ['Sequential', 'Loop', 'Conditional', 'Parallel', 'Escalation'];

        if (valid.includes(type)) {
            return type;
        }

        return 'Sequential';
    }

    /**
     * Transform date strings to Date objects
     */
    private transformDate(date: any): Date {
        if (!date) return new Date();
        if (date instanceof Date) return date;

        try {
            return new Date(date);
        } catch {
            return new Date();
        }
    }

    /**
     * Get empty process template
     */
    private getEmptyProcess(): ComplexProcessDefinition {
        return {
            id: asBackendId(''), // Empty ID will be replaced with backend-generated Snowflake ID on save
            tenant: sessionStorage.getItem('tenant') || 'default',
            name: 'New Process',
            domain: 'General',
            description: '',
            complexity: 'Low',
            avgDuration: undefined,
            stages: [],
            connections: [],
            actors: [],
            externalSystems: [],
            metrics: this.getDefaultMetrics(),
            isTemplate: false,
            version: '1.0.0',
            parentId: undefined,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: sessionStorage.getItem('user') || 'current-user',
            active: true,
            metadata: {}
        };
    }

    /**
     * Get default metrics
     */
    private getDefaultMetrics(): ProcessMetrics {
        return {
            totalSteps: 0,
            uniqueActors: 0,
            loopingStages: 0,
            externalIntegrations: 0,
            decisionPoints: 0,
            avgComplexityScore: 0,
            estimatedTotalDuration: undefined
        };
    }

    /**
     * Prepare process for GraphQL mutation
     */
    prepareForMutation(process: ComplexProcessDefinition): any {
        return {
            name: process.name,
            domain: process.domain,
            description: process.description,
            complexity: process.complexity,
            avgDuration: process.avgDuration,
            stages: process.stages.map(stage => ({
                id: stage.id,
                name: stage.name,
                stageType: stage.stageType,
                actors: stage.actors,
                steps: stage.steps.map(step => ({
                    id: step.id,
                    name: step.name,
                    actor: step.actor,
                    stepType: step.stepType,
                    external: step.external,
                    conditional: step.conditional,
                    loops: step.loops,
                    triggers: step.triggers,
                    outputs: step.outputs,
                    estimatedDuration: step.estimatedDuration
                })),
                loops: stage.loops,
                parallelTracks: stage.parallelTracks,
                estimatedDuration: stage.estimatedDuration,
                position: stage.position,
                color: stage.color
            })),
            connections: process.connections.map(conn => ({
                from: conn.from,
                to: conn.to,
                connectionType: conn.connectionType,
                condition: conn.condition,
                label: conn.label,
                probability: conn.probability,
                avgTransitionTime: conn.avgTransitionTime
            })),
            externalSystems: process.externalSystems.map(system => ({
                id: system.id,
                name: system.name,
                systemType: system.systemType,
                integrationPoints: system.integrationPoints,
                dataFormats: system.dataFormats,
                authentication: system.authentication
            })),
            isTemplate: process.isTemplate,
            metadata: process.metadata
        };
    }

    /**
     * Validate process data
     */
    validate(process: ComplexProcessDefinition): { valid: boolean; errors: string[] } {
        const errors: string[] = [];

        if (!process.name || process.name.trim() === '') {
            errors.push('Process name is required');
        }

        if (!process.domain) {
            errors.push('Process domain is required');
        }

        if (process.stages.length === 0) {
            errors.push('Process must have at least one stage');
        }

        // Check for orphaned connections
        const stageIds = process.stages.map(s => s.id);
        process.connections.forEach(conn => {
            // Type-safe comparison for BackendId (branded type)
            if (!stageIds.some(id => id === conn.from)) {
                errors.push(`Connection from unknown stage: ${conn.from}`);
            }
            if (!stageIds.some(id => id === conn.to)) {
                errors.push(`Connection to unknown stage: ${conn.to}`);
            }
        });

        // Check for entry and exit stages
        const hasEntry = process.stages.some(s => s.stageType === 'Entry');
        const hasExit = process.stages.some(s => s.stageType === 'Exit');

        if (!hasEntry) {
            errors.push('Process should have an Entry stage');
        }

        if (!hasExit) {
            errors.push('Process should have an Exit stage');
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }
}

export default ComplexProcessTransformer;
